import React from 'react';
import { useTranslation } from 'react-i18next';
import DeltaBadge from '../../Shared/DeltaBadge';
import { formatMoney, formatNumber } from '../../Shared/Formatters';

/**
 * KPI Widget — big number with a delta badge.
 * Auto-detects shape from service response:
 *   - {data: {count}}                       => Active Tenants
 *   - {data: {currentYear, growth}}         => Sales Summary
 *   - {data: {current, growthPct}}          => Revenue Summary
 *   - {data: {atv, totalSales, totalTxns}}  => ATV
 *   - {data: {daysLive}}                    => Days Live
 */
export default function KPIWidget({ data, widgetId }) {
    const { t } = useTranslation();
    if (!data?.data) return null;
    const d = data.data;

    let value = '—', delta = null, subtitle = '';

    if (d.count !== undefined) {
        value = formatNumber(d.count);
        subtitle = t('active tenants');
    } else if (d.currentPeriod?.totalSales !== undefined) {
        // Range-scoped sales summary (when a dashboard date filter is active).
        value = formatMoney(d.currentPeriod.totalSales);
        delta = d.growth?.salesPct;
        subtitle = t('vs prior period');
    } else if (d.currentYear?.totalSales !== undefined) {
        value = formatMoney(d.currentYear.totalSales);
        delta = d.growth?.salesPct;
        subtitle = t('YTD');
    } else if (d.current !== undefined) {
        value = formatMoney(d.current);
        delta = d.growthPct;
        subtitle = t('revenue share YTD');
    } else if (d.atv !== undefined) {
        value = formatMoney(d.atv);
        subtitle = t('{{total}} txns', { total: formatNumber(d.totalTxns) });
    } else if (d.daysLive !== undefined) {
        value = formatNumber(d.daysLive);
        subtitle = d.tenantName || t('days live');
    } else if (d.totalFootfall !== undefined) {
        value = formatNumber(d.totalFootfall);
        subtitle = t('visitors');
    } else if (d.shopperYield !== undefined) {
        value = formatMoney(d.shopperYield);
        subtitle = t('per visitor');
    }

    return (
        <div style={{ padding: '0 18px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <div className="v3-kpi">{value}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                {delta !== undefined && delta !== null && <DeltaBadge value={delta} suffix="%" />}
                {subtitle && <span className="v3-label" style={{ textTransform: 'none' }}>{subtitle}</span>}
            </div>
        </div>
    );
}
